// Notas Fiscais (0.26): emitir NFS-e pelo Padrão Nacional com o certificado A1, buscar as notas emitidas e
// recebidas (NFS-e no Ambiente Nacional; NF-e e CT-e na SEFAZ), importar XML e exportar em XML (.zip),
// planilha (.xlsx) e PDF. A conversa com o governo passa pelo servidor de notas (servidor-notas, Netlify).
import { CartaoApoio } from '../components/Recolhivel';
import { useState } from 'react';
import { ActivityIndicator, Alert, Pressable, ScrollView, Switch, Text, View } from 'react-native';
import type { Reg } from '../store/types';
import type { Colecao, Ctx, ModDef, PainelProps } from './tipos';
import { dataBr, din, junta, num, op } from './util';
import { mudarCampos, mudarReg, novoReg, regsDe, salvarReg, salvarVarios } from '../store/modulos';
import { useCofreSheet } from '../components/CofreSheet';
import { Btn, Chip, Field, Sheet } from '../components/ui';
import { PickerModal } from '../components/Pickers';
import { C, alpha } from '../components/theme';
import { anexoDeBytes, anexoDeTexto, abrirAnexo, apagarAnexo, escolherArquivo, lerBase64 } from '../lib/anexos';
import { semBloqueio } from '../auth/AuthContext';
import {
  ErroNotas, UFS, ambienteDe, camposDaNota, camposDoCertificado, cancelarNfse, capturarNfe, capturarNfse, cienciaNfe, conferirCertificado, danfse,
  deBase64, emitirNfse, exportarPdf, exportarXlsx, exportarZip, lerXml, mascaraDoc, servidorNotas, setServidorNotas, so, testarServidor, type ErroGov,
} from '../lib/fiscal';
import { brYmd, parseYmd, today, ymd } from '../lib/date';
import { useDoc } from '../store/store';
import { N_, t } from '../lib/i18n';
import { ROLAGEM_TECLADO } from '../components/teclado';

const AMBIENTES = [N_('Produção restrita (teste, não vale)'), N_('Produção (vale de verdade)')];
const REGIMES = [N_('MEI'), N_('ME ou EPP do Simples'), N_('Não optante pelo Simples')];
const STATUS = [N_('Emitida'), N_('Recebida'), N_('Resumo (falta o XML completo)'), N_('Cancelada')];
const erro = (e: any) => Alert.alert(t('Não foi possível'), String(e?.message ?? e));
const listaErros = (l: ErroGov[] = []) => l.map(e => `• ${e.codigo ? e.codigo + ': ' : ''}${e.descricao}${e.complemento ? ` (${e.complemento})` : ''}`).join('\n');
const empresaDe = (ctx: Ctx, r: Reg | null) => { let p = r; while (p && p.col !== 'empresa') p = ctx.porId(p.pai); return p; };
const textoDeB64 = (b64: string) => new TextDecoder().decode(deBase64(b64));

/** Pede a senha do cofre quando a ação precisa do certificado (que fica criptografado no aparelho). */
async function comCofre<T>(pedir: () => Promise<boolean>, fn: () => Promise<T>): Promise<T | undefined> {
  try { return await fn(); }
  catch (e: any) {
    if (String(e?.message) !== 'cofre-fechado') throw e;
    if (!(await pedir())) return undefined;
    return fn();
  }
}

/** Grava (ou atualiza pela chave) uma nota vinda do XML, com o arquivo XML anexado. */
async function guardarNota(ctx: Ctx, empresa: Reg, xml: string, completo = true, extra: Record<string, any> = {}): Promise<'nova' | 'atualizada' | 'ignorada'> {
  const r = lerXml(xml);
  if (!r) return 'ignorada';
  const campos = { ...camposDaNota(r, empresa.c.documento, completo), ...extra };
  const ja = r.chave ? regsDe(ctx.mod).find(x => x.col === 'nota' && x.pai === empresa.id && x.c.chave === r.chave) : null;
  if (ja && !completo) return 'ignorada';
  const anexo = completo ? await anexoDeTexto(`${r.tipo}-${r.numero || r.chave || 'nota'}.xml`, xml) : null;
  if (ja) {
    (ja.anexos ?? []).filter(a => a.mime === 'application/xml').forEach(apagarAnexo);
    mudarReg(ctx.mod, ja.id, x => ({ ...x, c: { ...x.c, ...campos, status: x.c.status === 'Cancelada' ? 'Cancelada' : campos.status }, anexos: [...(x.anexos ?? []).filter(a => a.mime !== 'application/xml'), ...(anexo ? [anexo] : [])] }));
    return 'atualizada';
  }
  salvarReg(ctx.mod, { ...novoReg('nota', empresa.id, campos), anexos: anexo ? [anexo] : [] });
  return 'nova';
}

// ─── Painel dentro da empresa: certificado, emitir, buscar, importar, exportar ───────────────────
function PainelEmpresa({ ctx, pai }: PainelProps) {
  const empresa = pai;
  useDoc('ajustes');   // re-renderiza quando o endereço do servidor muda
  const cofre = useCofreSheet(ctx.cor);
  const [busy, setBusy] = useState('');
  const [cert, setCert] = useState<{ b64: string; nome: string } | null>(null);
  const [senha, setSenha] = useState('');
  const [emitir, setEmitir] = useState(false);
  const [url, setUrl] = useState('');
  const [periodo, setPeriodo] = useState<'mes' | 'anterior' | 'ano' | 'tudo'>('mes');
  const [direcao, setDirecao] = useState<'todas' | 'Emitida' | 'Recebida'>('todas');
  if (!empresa || empresa.col !== 'empresa') return null;
  const cor = ctx.cor;
  const servidor = servidorNotas();
  const amb = ambienteDe(empresa);
  const venc = empresa.c.certValidade ? Math.round((parseYmd(empresa.c.certValidade).getTime() - Date.now()) / 864e5) : null;

  async function escolherCertificado() {
    try {
      const a = await semBloqueio(() => escolherArquivo(false, true));
      if (!a) return;
      const b64 = await lerBase64(a);
      apagarAnexo(a);   // a cópia solta do arquivo sai; ele fica só criptografado no cofre
      setCert({ b64, nome: a.nome }); setSenha('');
    } catch (e) { erro(e); }
  }
  async function salvarCertificado() {
    if (!cert || !senha) return;
    setBusy('cert');
    try {
      const info = await conferirCertificado(cert.b64, senha);
      if (empresa!.c.documento && info.documento && so(info.documento) !== so(empresa!.c.documento))
        throw new ErroNotas(t('Este certificado é de {doc}, diferente do CNPJ/CPF da empresa ({emp}).', { doc: mascaraDoc(info.documento), emp: mascaraDoc(empresa!.c.documento) }));
      if (!(await cofre.pedir())) return;
      mudarCampos(ctx.mod, empresa!.id, { ...camposDoCertificado(cert.b64, senha, info), ...(empresa!.c.documento ? {} : { documento: info.documento }), ...(empresa!.c.nome ? {} : { nome: info.titular }) });
      setCert(null); setSenha('');
      Alert.alert(t('Certificado ligado'), t('{nome}\nVale até {d}. Ele fica criptografado no seu cofre, só neste dispositivo e na sua cópia da nuvem.', { nome: info.titular, d: dataBr(info.validade.slice(0, 10)) }));
    } catch (e) { erro(e); }
    finally { setBusy(''); }
  }

  async function buscar() {
    setBusy('buscar');
    try {
      const r = await comCofre(cofre.pedir, async () => {
        let novas = 0, canceladas = 0;
        const avisos: string[] = [];
        // NFS-e (Ambiente Nacional): emitidas por você e contra você
        const nf = await capturarNfse(empresa!, num(empresa!.c.nsuNfse));
        for (const d of nf.docs) {
          if (d.tipo === 'NFSE' && d.xml) { if ((await guardarNota(ctx, empresa!, d.xml)) === 'nova') novas++; }
          else if (d.tipo === 'EVENTO' && /CANCELAMENTO/.test(String(d.evento)) && d.chave) {
            const n = regsDe(ctx.mod).find(x => x.col === 'nota' && x.c.chave === d.chave);
            if (n && n.c.status !== 'Cancelada') { mudarCampos(ctx.mod, n.id, { status: 'Cancelada' }); canceladas++; }
          }
        }
        mudarCampos(ctx.mod, empresa!.id, { nsuNfse: nf.ultNSU });
        if (nf.status === 'REJEICAO') avisos.push(t('NFS-e: {m}', { m: nf.avisos.join(' ') || t('consulta recusada') }));
        // NF-e e CT-e (SEFAZ): só de hora em hora, para a SEFAZ não bloquear o CNPJ
        const espera = num(empresa!.c.nfeEspera);
        if (espera > Date.now()) avisos.push(t('NF-e: a SEFAZ pede 1 hora entre as buscas. Próxima a partir das {h}.', { h: new Date(espera).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) }));
        else if (empresa!.c.uf) {
          const ne = await capturarNfe(empresa!, String(empresa!.c.nsuNfe || '0'));
          for (const d of ne.docs) {
            if (/resNFe/.test(String(d.schema))) { if ((await guardarNota(ctx, empresa!, d.xml, false)) === 'nova') novas++; }
            else if (/procNFe|procCTe|nfeProc|cteProc/.test(String(d.schema))) { if ((await guardarNota(ctx, empresa!, d.xml)) === 'nova') novas++; }
          }
          const esperar = ne.cStat === '137' || ne.cStat === '656' || Number(ne.ultNSU) >= Number(ne.maxNSU || 0);
          mudarCampos(ctx.mod, empresa!.id, { nsuNfe: ne.ultNSU, nfeEspera: esperar ? Date.now() + 61 * 60 * 1000 : 0 });
          if (!['137', '138'].includes(ne.cStat)) avisos.push(`NF-e: ${ne.cStat} ${ne.xMotivo}`);
        } else avisos.push(t('NF-e: informe a UF da empresa para buscar as notas de produtos.'));
        return { novas, canceladas, avisos };
      });
      if (r) Alert.alert(t('Busca de notas'), [t('{n} nota(s) nova(s).', { n: r.novas }), r.canceladas ? t('{n} cancelada(s).', { n: r.canceladas }) : '', ...r.avisos].filter(Boolean).join('\n'));
    } catch (e) { erro(e); }
    finally { setBusy(''); }
  }

  async function importar() {
    try {
      const a = await semBloqueio(() => escolherArquivo(false, true));
      if (!a) return;
      const xml = textoDeB64(await lerBase64(a));
      apagarAnexo(a);
      const r = await guardarNota(ctx, empresa!, xml);
      Alert.alert(r === 'ignorada' ? t('Não é um XML de nota fiscal') : t('Nota importada'), r === 'ignorada' ? t('Escolha o arquivo .xml da NFS-e, NF-e ou CT-e.') : '');
    } catch (e) { erro(e); }
  }

  const notas = ctx.filhos(empresa.id, 'nota').filter(n => {
    const d = String(n.c.data || ''), hoje = today();
    const mes = hoje.slice(0, 7), ant = ymd(new Date(parseYmd(hoje).getFullYear(), parseYmd(hoje).getMonth() - 1, 1)).slice(0, 7);
    const noPeriodo = periodo === 'tudo' || (periodo === 'mes' && d.startsWith(mes)) || (periodo === 'anterior' && d.startsWith(ant)) || (periodo === 'ano' && d.startsWith(hoje.slice(0, 4)));
    return noPeriodo && (direcao === 'todas' || n.c.direcao === direcao);
  }).sort((a, b) => String(a.c.data).localeCompare(String(b.c.data)));
  const nomeExp = `${t('Notas')} ${empresa.c.nome || ''} ${periodo === 'mes' ? today().slice(0, 7) : periodo === 'ano' ? today().slice(0, 4) : periodo}`;
  const exportar = async (tipo: 'zip' | 'xlsx' | 'pdf') => {
    if (!notas.length) return Alert.alert(t('Nenhuma nota neste período.'));
    setBusy('exp');
    try {
      if (tipo === 'zip') { const faltam = await exportarZip(notas, nomeExp); if (faltam) Alert.alert(t('{n} nota(s) sem XML neste dispositivo ficaram de fora.', { n: faltam })); }
      else if (tipo === 'xlsx') await exportarXlsx(notas, nomeExp);
      else await exportarPdf(notas, nomeExp, empresa, v => din(v, ctx.moeda));
    } catch (e) { erro(e); }
    finally { setBusy(''); }
  };

  return (
    <View style={{ gap: 10 }}>
      {!servidor && (
        <View style={{ backgroundColor: alpha(C.warn, 0.12), borderRadius: 14, padding: 12, gap: 8 }}>
          <Text style={{ color: C.text, fontWeight: '800' }}>🔌 {t('Servidor de notas')}</Text>
          <Text style={{ color: C.sub, fontSize: 12 }}>{t('Para emitir e buscar notas, ligue uma vez o servidor de notas: no computador, dê dois cliques em 13-LIGAR-NOTAS.bat e cole aqui o endereço que ele mostrar (termina em .netlify.app).')}</Text>
          <Field value={url} onChangeText={setUrl} placeholder="https://...netlify.app" autoCapitalize="none" autoCorrect={false} />
          <Btn cor={cor} label={busy === 'url' ? t('Testando...') : t('Salvar endereço')} disabled={busy === 'url'} onPress={async () => {
            setBusy('url');
            try { if (!(await testarServidor(url))) throw new Error(t('Esse endereço não respondeu como servidor de notas. Confira e tente de novo.')); setServidorNotas(url); }
            catch (e) { erro(e); } finally { setBusy(''); }
          }} />
        </View>
      )}

      <CartaoApoio titulo={'🔐 ' + t('Certificado digital A1')} chave={'notas:cert'} resumo={empresa.c._pfx ? t('Vale até {d}', { d: dataBr(empresa.c.certValidade) }) : ''} gap={6}>
        {empresa.c._pfx ? (<>
          <Text style={{ color: C.text, fontSize: 13 }}>{empresa.c.certTitular} · {mascaraDoc(empresa.c.certDoc)}</Text>
          <Text style={{ color: venc !== null && venc < 30 ? C.warn : C.sub, fontSize: 12 }}>{t('Vale até {d}', { d: dataBr(empresa.c.certValidade) })}{venc !== null && venc < 30 ? ` · ${venc < 0 ? t('vencido') : t('vence em {n} dias', { n: venc })}` : ''}</Text>
        </>) : <Text style={{ color: C.sub, fontSize: 12 }}>{t('Escolha o arquivo .pfx (ou .p12) do certificado A1 da empresa e digite a senha dele. Ele fica criptografado no seu cofre.')}</Text>}
        {!cert && <Chip small cor={cor} on={!empresa.c._pfx} label={empresa.c._pfx ? '🔁 ' + t('Trocar o certificado') : '📂 ' + t('Escolher o certificado')} onPress={escolherCertificado} />}
        {!!cert && (<>
          <Text style={{ color: C.text, fontSize: 12 }}>📄 {cert.nome}</Text>
          <Field value={senha} onChangeText={setSenha} placeholder={t('Senha do certificado')} secureTextEntry autoCapitalize="none" onSubmitEditing={salvarCertificado} />
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <Btn cor={cor} label={busy === 'cert' ? t('Conferindo...') : t('Ligar certificado')} disabled={!senha || busy === 'cert'} onPress={salvarCertificado} style={{ flex: 1 }} />
            <Btn outline cor={C.sub} label={t('Cancelar')} onPress={() => setCert(null)} />
          </View>
        </>)}
      </CartaoApoio>

      <View style={{ backgroundColor: amb === 'producao' ? alpha(C.danger, 0.1) : alpha(cor, 0.1), borderRadius: 12, padding: 10 }}>
        <Text style={{ color: amb === 'producao' ? C.danger : C.text, fontSize: 12, fontWeight: '700' }}>
          {amb === 'producao' ? '⚠️ ' + t('Produção: as notas emitidas valem de verdade e vão para a prefeitura e a Receita.') : '🧪 ' + t('Produção restrita: ambiente de teste do governo. As notas não valem. Teste tudo aqui antes de mudar para Produção (em Editar).')}
        </Text>
      </View>

      <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
        <Btn cor={cor} label={'🧾 ' + t('Emitir NFS-e')} onPress={() => (empresa.c._pfx ? setEmitir(true) : Alert.alert(t('Ligue o certificado A1 da empresa primeiro.')))} style={{ flexGrow: 1 }} />
        <Btn outline cor={cor} label={busy === 'buscar' ? t('Buscando...') : '🔄 ' + t('Buscar notas')} disabled={busy === 'buscar'} onPress={() => (empresa.c._pfx ? buscar() : Alert.alert(t('Ligue o certificado A1 da empresa primeiro.')))} style={{ flexGrow: 1 }} />
      </View>
      <Chip small cor={cor} on={false} label={'📥 ' + t('Importar XML de nota')} onPress={importar} />

      <CartaoApoio titulo={'📤 ' + t('Exportar para o contador')} chave={'notas:exportar'} gap={8}>
        <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
          {([['mes', N_('Este mês')], ['anterior', N_('Mês passado')], ['ano', N_('Este ano')], ['tudo', N_('Tudo')]] as const).map(([k, l]) => <Chip key={k} small cor={cor} on={periodo === k} label={t(l)} onPress={() => setPeriodo(k)} />)}
        </View>
        <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
          {([['todas', N_('Todas')], ['Emitida', N_('Emitidas')], ['Recebida', N_('Recebidas')]] as const).map(([k, l]) => <Chip key={k} small cor={cor} on={direcao === k} label={t(l)} onPress={() => setDirecao(k)} />)}
        </View>
        <Text style={{ color: C.sub, fontSize: 12 }}>{t('{n} nota(s) · emitidas {e} · recebidas {r}', { n: notas.length,
          e: din(notas.filter(n => n.c.direcao === 'Emitida' && n.c.status !== 'Cancelada').reduce((s, n) => s + num(n.c.valor), 0), ctx.moeda),
          r: din(notas.filter(n => n.c.direcao === 'Recebida' && n.c.status !== 'Cancelada').reduce((s, n) => s + num(n.c.valor), 0), ctx.moeda) })}</Text>
        {busy === 'exp' ? <ActivityIndicator color={cor} /> : (
          <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
            <Chip small cor={cor} on={false} label="🗂️ XML (.zip)" onPress={() => exportar('zip')} />
            <Chip small cor={cor} on={false} label={'📊 ' + t('Planilha (Excel)')} onPress={() => exportar('xlsx')} />
            <Chip small cor={cor} on={false} label="📄 PDF" onPress={() => exportar('pdf')} />
          </View>
        )}
      </CartaoApoio>
      {emitir && <EmitirSheet ctx={ctx} empresa={empresa} pedirCofre={cofre.pedir} onClose={() => setEmitir(false)} />}
      {cofre.tela}
    </View>
  );
}

// ─── Emitir a NFS-e ───────────────────────────────────────────────────────────────────────────────
function EmitirSheet({ ctx, empresa, pedirCofre, onClose }: { ctx: Ctx; empresa: Reg; pedirCofre: () => Promise<boolean>; onClose: () => void }) {
  const cor = ctx.cor;
  const tomadores = ctx.filhos(empresa.id, 'tomador'), servicos = ctx.filhos(empresa.id, 'servico');
  const [tomador, setTomador] = useState<Reg | null>(null);
  const [desc, setDesc] = useState(''); const [cTrib, setCTrib] = useState(''); const [cNBS, setCNBS] = useState(''); const [local, setLocal] = useState(String(empresa.c.cMun ?? ''));
  const [valor, setValor] = useState(''); const [desconto, setDesconto] = useState(''); const [aliq, setAliq] = useState(empresa.c.aliquota ? String(empresa.c.aliquota) : '');
  const [compet, setCompet] = useState(today()); const [retido, setRetido] = useState(false); const [data, setData] = useState(false);
  const [busy, setBusy] = useState(false);
  const [falhas, setFalhas] = useState<ErroGov[] | null>(null);
  const amb = ambienteDe(empresa);
  const usarServico = (s: Reg) => { setDesc(s.c.descricao || ''); setCTrib(s.c.cTribNac || ''); setCNBS(s.c.cNBS || ''); if (s.c.local) setLocal(s.c.local); if (s.c.valor) setValor(String(s.c.valor).replace('.', ',')); if (s.c.aliquota) setAliq(String(s.c.aliquota)); };
  const n = (s: string) => Number(String(s).replace(/\./g, '').replace(',', '.')) || 0;

  async function enviar() {
    setBusy(true); setFalhas(null);
    try {
      const r = await comCofre(pedirCofre, () => emitirNfse(empresa, { tomador, servico: { descricao: desc, cTribNac: cTrib, cNBS, cLocPrestacao: local }, valor: n(valor), desconto: n(desconto), competencia: compet, issRetido: retido, aliquota: aliq ? n(aliq) : undefined }));
      if (!r) return;
      if (!r.ok || !r.xml) { setFalhas(r.erros ?? [{ codigo: '', descricao: t('O governo recusou a nota.') }]); return; }
      await guardarNota(ctx, empresa, r.xml, true, { ambiente: amb === 'producao' ? 'Produção' : 'Produção restrita' });
      mudarCampos(ctx.mod, empresa.id, { proximo: Math.max(1, num(empresa.c.proximo)) + 1 });
      onClose();
      Alert.alert(t('NFS-e emitida'), t('Número {n}. O XML ficou guardado na nota. Toque nela para ver o PDF.', { n: r.resumo?.numero || '?' }));
    } catch (e) { erro(e); }
    finally { setBusy(false); }
  }
  const confirmar = () => {
    if (!desc.trim() || !/^\d{6}$/.test(so(cTrib)) || !(n(valor) > 0)) return Alert.alert(t('Preencha a descrição, o código de tributação nacional (6 números) e o valor.'));
    Alert.alert(amb === 'producao' ? t('Emitir a nota de verdade?') : t('Emitir no ambiente de teste?'),
      `${tomador ? tomador.c.nome : t('Sem tomador identificado')}\n${din(n(valor), ctx.moeda)} · ${desc.trim().slice(0, 80)}\n${t('DPS nº {n}, série {s}', { n: Math.max(1, num(empresa.c.proximo)), s: empresa.c.serie || '1' })}`,
      [{ text: t('Cancelar'), style: 'cancel' }, { text: t('Emitir'), onPress: enviar }]);
  };
  return (
    <Sheet visible onClose={onClose} title={t('Emitir NFS-e')}>
      <ScrollView {...ROLAGEM_TECLADO} keyboardShouldPersistTaps="handled" contentContainerStyle={{ padding: 16, gap: 10, paddingBottom: 80 }}>
        <Text style={{ color: amb === 'producao' ? C.danger : C.sub, fontSize: 12, fontWeight: '700' }}>{amb === 'producao' ? '⚠️ ' + t('Produção: vale de verdade') : '🧪 ' + t('Produção restrita (teste)')}</Text>
        <Text style={{ color: C.sub, fontSize: 13 }}>{t('Tomador (quem contratou)')}</Text>
        <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
          <Chip small cor={cor} on={!tomador} label={t('Não identificado')} onPress={() => setTomador(null)} />
          {tomadores.map(x => <Chip key={x.id} small cor={cor} on={tomador?.id === x.id} label={x.c.nome || mascaraDoc(x.c.documento)} onPress={() => setTomador(x)} />)}
        </View>
        {!tomadores.length && <Text style={{ color: C.mute, fontSize: 12 }}>{t('Cadastre seus clientes na aba "Tomadores" da empresa para escolher aqui.')}</Text>}
        {!!servicos.length && (<>
          <Text style={{ color: C.sub, fontSize: 13 }}>{t('Serviço')}</Text>
          <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>{servicos.map(s => <Chip key={s.id} small cor={cor} on={desc === s.c.descricao} label={s.c.nome || s.c.descricao} onPress={() => usarServico(s)} />)}</View>
        </>)}
        <Field multiline value={desc} onChangeText={setDesc} placeholder={t('Descrição do serviço (sai na nota)')} style={{ minHeight: 80, textAlignVertical: 'top' }} />
        <Field value={cTrib} onChangeText={setCTrib} placeholder={t('Código de tributação nacional (6 números, ex.: 171901)')} keyboardType="number-pad" />
        <Field value={cNBS} onChangeText={setCNBS} placeholder={t('Código NBS (9 números, opcional)')} keyboardType="number-pad" />
        <Field value={local} onChangeText={setLocal} placeholder={t('Município da prestação (código IBGE, 7 números)')} keyboardType="number-pad" />
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <Field value={valor} onChangeText={setValor} placeholder={t('Valor')} keyboardType="decimal-pad" style={{ flex: 1.3, minWidth: 0 }} />
          <Field value={desconto} onChangeText={setDesconto} placeholder={t('Desconto')} keyboardType="decimal-pad" style={{ flex: 1, minWidth: 0 }} />
          <Field value={aliq} onChangeText={setAliq} placeholder={t('ISS %')} keyboardType="decimal-pad" style={{ flex: 0.8, minWidth: 0 }} />
        </View>
        <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
          <Chip cor={cor} on label={'📅 ' + t('Competência {d}', { d: brYmd(compet) })} onPress={() => setData(true)} />
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <Text style={{ color: C.text, flex: 1 }}>{t('ISS retido pelo tomador')}</Text>
          <Switch value={retido} onValueChange={setRetido} trackColor={{ true: cor }} />
        </View>
        {!!falhas && (
          <View style={{ backgroundColor: alpha(C.danger, 0.1), borderRadius: 12, padding: 10, gap: 4 }}>
            <Text style={{ color: C.danger, fontWeight: '800' }}>{t('O governo recusou a nota')}</Text>
            <Text style={{ color: C.text, fontSize: 12 }} selectable>{listaErros(falhas)}</Text>
          </View>
        )}
        {busy ? <ActivityIndicator color={cor} /> : <Btn cor={amb === 'producao' ? C.danger : cor} label={'🧾 ' + t('Emitir')} onPress={confirmar} />}
        {data && <PickerModal mode="date" cor={cor} value={parseYmd(compet)} onPick={d => setCompet(ymd(d) > today() ? today() : ymd(d))} onClose={() => setData(false)} />}
      </ScrollView>
    </Sheet>
  );
}

// ─── Ações da nota ───────────────────────────────────────────────────────────────────────────────
function acoesNota(r: Reg, ctx: Ctx) {
  const empresa = empresaDe(ctx, r);
  const xml = (r.anexos ?? []).find(a => a.mime === 'application/xml');
  const pdf = (r.anexos ?? []).find(a => a.mime === 'application/pdf');
  const out: { label: string; onPress: () => void; destructive?: boolean }[] = [];
  if (pdf) out.push({ label: '📄 ' + t('Abrir o PDF'), onPress: () => semBloqueio(() => abrirAnexo(pdf)).catch(erro) });
  if (xml) out.push({ label: '🗂️ ' + t('Abrir o XML'), onPress: () => semBloqueio(() => abrirAnexo(xml)).catch(erro) });
  if (!empresa) return out;
  if (r.c.tipo === 'NFS-e' && r.c.chave && !pdf) out.push({ label: '📄 ' + t('Baixar o PDF da nota (DANFSe)'), onPress: async () => {
    try {
      const res = await danfse(empresa, r.c.chave);
      if (!res.ok || !res.pdf) throw new Error(listaErros(res.erros));
      const a = await anexoDeBytes(`NFS-e ${r.c.numero || r.c.chave}.pdf`, deBase64(res.pdf), 'application/pdf');
      mudarReg(ctx.mod, r.id, x => ({ ...x, anexos: [...(x.anexos ?? []), a] }));
      semBloqueio(() => abrirAnexo(a)).catch(() => {});
    } catch (e: any) { if (String(e?.message) === 'cofre-fechado') Alert.alert(t('Abra o cofre'), t('Abra a empresa e toque em "Buscar notas" para digitar a senha do cofre.')); else erro(e); }
  } });
  if (r.c.tipo === 'NF-e' && String(r.c.status).startsWith('Resumo') && r.c.chave) out.push({ label: '⬇️ ' + t('Baixar o XML completo (dá ciência na SEFAZ)'), onPress: () =>
    Alert.alert(t('Dar ciência da operação?'), t('A SEFAZ registra que a nota chegou ao seu conhecimento (não confirma nem recusa a compra) e libera o XML completo na próxima busca, em até 1 hora.'), [
      { text: t('Cancelar'), style: 'cancel' },
      { text: t('Dar ciência'), onPress: async () => {
        try { const res = await cienciaNfe(empresa, r.c.chave); Alert.alert(['135', '136', '573'].includes(res.cStat) ? t('Pronto') : t('Resposta da SEFAZ'), `${res.cStat} ${res.xMotivo}`); mudarCampos(ctx.mod, empresa.id, { nfeEspera: 0 }); }
        catch (e: any) { if (String(e?.message) === 'cofre-fechado') Alert.alert(t('Abra o cofre'), t('Abra a empresa e toque em "Buscar notas" para digitar a senha do cofre.')); else erro(e); }
      } }]) });
  if (r.c.tipo === 'NFS-e' && r.c.direcao === 'Emitida' && r.c.status === 'Emitida' && r.c.chave) out.push({ label: '🚫 ' + t('Cancelar a NFS-e'), destructive: true, onPress: () => {
    Alert.alert(t('Cancelar a NFS-e {n}?', { n: r.c.numero || '' }), t('Escreva o motivo em "Motivo do cancelamento" na ficha da nota (pelo menos 15 letras) e toque aqui de novo.'), [
      { text: t('Voltar'), style: 'cancel' },
      ...(String(r.c.motivo ?? '').trim().length >= 15 ? [{ text: t('Cancelar a nota'), style: 'destructive' as const, onPress: async () => {
        try {
          const res = await cancelarNfse(empresa, r.c.chave, r.c.motivoCod === 'Serviço não prestado' ? '2' : r.c.motivoCod === 'Outros' ? '9' : '1', r.c.motivo);
          if (!res.ok) throw new Error(listaErros(res.erros));
          const ev = res.xmlEvento ? await anexoDeTexto(`Cancelamento ${r.c.numero || r.c.chave}.xml`, res.xmlEvento) : null;
          mudarReg(ctx.mod, r.id, x => ({ ...x, c: { ...x.c, status: 'Cancelada' }, anexos: [...(x.anexos ?? []), ...(ev ? [ev] : [])] }));
          Alert.alert(t('Nota cancelada'));
        } catch (e: any) { if (String(e?.message) === 'cofre-fechado') Alert.alert(t('Abra o cofre'), t('Abra a empresa e toque em "Buscar notas" para digitar a senha do cofre.')); else erro(e); }
      } }] : []),
    ]);
  } });
  return out;
}

// ─── Coleções ────────────────────────────────────────────────────────────────────────────────────
const empresa: Colecao = {
  id: 'empresa', nome: N_('Empresas'), um: N_('Empresa'), icone: '🏢',
  campos: [
    { k: 'nome', r: N_('Razão social ou nome'), t: 'texto' },
    { k: 'documento', r: N_('CNPJ ou CPF'), t: 'texto' },
    { k: 'im', r: N_('Inscrição municipal (se tiver)'), t: 'texto' },
    { k: 'uf', r: N_('UF'), t: 'opcao', op: UFS },
    { k: 'cidade', r: N_('Cidade'), t: 'texto' },
    { k: 'cMun', r: N_('Código IBGE do município (7 números)'), t: 'texto', ph: N_('Ex.: 4106902 (Curitiba), 5002704 (Campo Grande), 3550308 (São Paulo)') },
    { k: 'regime', r: N_('Simples Nacional'), t: 'opcao', op: REGIMES },
    { k: 'aliquota', r: N_('Alíquota do ISS (%)'), t: 'numero', un: '%', mostrar: c => c.regime !== 'MEI' },
    { k: 'pTotTribSN', r: N_('Percentual de tributos do Simples (para sair na nota)'), t: 'numero', un: '%', mostrar: c => c.regime === 'ME ou EPP do Simples' },
    { k: 'serie', r: N_('Série da DPS'), t: 'texto', ph: '1' },
    { k: 'proximo', r: N_('Próximo número da DPS'), t: 'numero' },
    { k: 'ambiente', r: N_('Ambiente'), t: 'opcao', op: AMBIENTES },
    { k: 'email', r: N_('E-mail'), t: 'email' },
    { k: 'fone', r: N_('Telefone'), t: 'telefone' },
    { k: 'certValidade', r: N_('Validade do certificado A1'), t: 'data', aviso: [30, 7, 0] },
  ],
  titulo: r => r.c.nome || mascaraDoc(r.c.documento),
  sub: r => junta(mascaraDoc(r.c.documento), op(r.c.regime), r.c.cidade && `${r.c.cidade}${r.c.uf ? '/' + r.c.uf : ''}`, ambienteDe(r) === 'producao' ? '✅ ' + t('produção') : '🧪 ' + t('teste')),
  novo: () => ({ ambiente: AMBIENTES[0], serie: '1', proximo: 1, regime: 'ME ou EPP do Simples' }),
  ajustar: (c, k) => (k === 'documento' ? { ...c, documento: mascaraDoc(so(c.documento)) } : c),
  vazio: N_('Cadastre a empresa (ou você, como profissional) com CNPJ ou CPF, município e regime. Depois ligue o certificado A1 para emitir NFS-e pelo Padrão Nacional e buscar as notas emitidas e recebidas.'),
};

const servico: Colecao = {
  id: 'servico', pai: 'empresa', nome: N_('Serviços'), um: N_('Serviço'), icone: '🛠️',
  campos: [
    { k: 'nome', r: N_('Nome curto'), t: 'texto', ph: N_('Ex.: Consultoria mensal') },
    { k: 'descricao', r: N_('Descrição que sai na nota'), t: 'longo', simples: true },
    { k: 'cTribNac', r: N_('Código de tributação nacional (6 números)'), t: 'texto', ph: N_('Ex.: 171901 (consultoria), 010101 (programação)') },
    { k: 'cNBS', r: N_('Código NBS (9 números, opcional)'), t: 'texto' },
    { k: 'local', r: N_('Município da prestação (IBGE), se for outro'), t: 'texto' },
    { k: 'valor', r: N_('Valor de costume'), t: 'dinheiro' },
    { k: 'aliquota', r: N_('Alíquota do ISS deste serviço (%)'), t: 'numero', un: '%' },
  ],
  titulo: r => r.c.nome || r.c.descricao || '', sub: (r, ctx) => junta(r.c.cTribNac, r.c.valor && din(r.c.valor, ctx.moeda)),
  vazio: N_('Cadastre os serviços que você mais emite, com o código de tributação nacional. Na hora de emitir é só tocar.'),
};

const tomador: Colecao = {
  id: 'tomador', pai: 'empresa', nome: N_('Tomadores'), um: N_('Tomador'), icone: '🤝',
  campos: [
    { k: 'nome', r: N_('Nome ou razão social'), t: 'texto' },
    { k: 'documento', r: N_('CNPJ ou CPF'), t: 'texto' },
    { k: 'email', r: N_('E-mail (recebe a nota)'), t: 'email' },
    { k: 'fone', r: N_('Telefone'), t: 'telefone' },
    { k: 'cep', r: N_('CEP'), t: 'texto' },
    { k: 'cMun', r: N_('Código IBGE do município'), t: 'texto' },
    { k: 'logradouro', r: N_('Rua'), t: 'texto' },
    { k: 'numero', r: N_('Número'), t: 'texto' },
    { k: 'complemento', r: N_('Complemento'), t: 'texto' },
    { k: 'bairro', r: N_('Bairro'), t: 'texto' },
  ],
  titulo: r => r.c.nome || '', sub: r => junta(mascaraDoc(r.c.documento), r.c.email),
  ajustar: (c, k) => (k === 'documento' ? { ...c, documento: mascaraDoc(so(c.documento)) } : c),
  vazio: N_('Cadastre os clientes que recebem as suas notas. O endereço é opcional (se preencher, informe CEP, código IBGE, rua, número e bairro).'),
};

const nota: Colecao = {
  id: 'nota', pai: 'empresa', nome: N_('Notas'), um: N_('Nota fiscal'), anexos: true, ordem: (a, b) => String(b.c.data ?? '').localeCompare(String(a.c.data ?? '')),
  icone: r => (r.c.status === 'Cancelada' ? '🚫' : String(r.c.status).startsWith('Resumo') ? '📨' : r.c.direcao === 'Emitida' ? '📤' : '📥'),
  campos: [
    { k: 'direcao', r: N_('Emitida ou recebida'), t: 'opcao', op: [N_('Emitida'), N_('Recebida')] },
    { k: 'tipo', r: N_('Tipo'), t: 'opcao', op: ['NFS-e', 'NF-e', 'CT-e', N_('Outra')] },
    { k: 'numero', r: N_('Número'), t: 'texto' },
    { k: 'data', r: N_('Data'), t: 'data' },
    { k: 'contraparte', r: N_('Nome (cliente ou fornecedor)'), t: 'texto' },
    { k: 'contraparteDoc', r: N_('CNPJ ou CPF'), t: 'texto' },
    { k: 'descricao', r: N_('Descrição'), t: 'longo', simples: true },
    { k: 'valor', r: N_('Valor'), t: 'dinheiro' },
    { k: 'iss', r: N_('ISS'), t: 'dinheiro' },
    { k: 'status', r: N_('Situação'), t: 'opcao', op: STATUS },
    { k: 'chave', r: N_('Chave de acesso'), t: 'texto' },
    { k: 'motivoCod', r: N_('Motivo do cancelamento'), t: 'opcao', op: [N_('Erro na emissão'), N_('Serviço não prestado'), N_('Outros')], mostrar: c => c.direcao === 'Emitida' && c.tipo === 'NFS-e' },
    { k: 'motivo', r: N_('Explique o motivo (para cancelar)'), t: 'longo', simples: true, mostrar: c => c.direcao === 'Emitida' && c.tipo === 'NFS-e' },
  ],
  titulo: r => junta(r.c.tipo, r.c.numero && `nº ${r.c.numero}`) || t('Nota fiscal'),
  sub: (r, ctx) => junta(dataBr(r.c.data), r.c.contraparte, din(r.c.valor, ctx.moeda), op(r.c.status), r.c.ambiente === 'Produção restrita' && '🧪'),
  filtros: [[N_('Emitidas'), r => r.c.direcao === 'Emitida'], [N_('Recebidas'), r => r.c.direcao === 'Recebida'], [N_('Falta XML'), r => String(r.c.status).startsWith('Resumo')], [N_('Canceladas'), r => r.c.status === 'Cancelada']],
  acoes: acoesNota,
  resumo: (l, ctx) => {
    const mes = today().slice(0, 7), doMes = l.filter(n => String(n.c.data).startsWith(mes) && n.c.status !== 'Cancelada');
    const s = (d: string) => doMes.filter(n => n.c.direcao === d).reduce((a, n) => a + num(n.c.valor), 0);
    return doMes.length ? t('Este mês: emitidas {e} · recebidas {r}', { e: din(s('Emitida'), ctx.moeda), r: din(s('Recebida'), ctx.moeda) }) : null;
  },
  painel: PainelEmpresa,
  vazio: N_('As notas emitidas aqui, as encontradas na busca e os XML importados aparecem nesta lista.'),
};

export const NOTAS: ModDef = { id: 'notas', colecoes: [empresa, nota, servico, tomador] };
void salvarVarios;
